'use client';

import React, { useState, useEffect } from 'react';
import { FaCalculator, FaChartPie, FaMoneyBill, FaUsers, FaPercentage, FaQuestionCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { useTheme } from 'next-themes';

const CommissionCalculator = () => {
  const [salesAmount, setSalesAmount] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [fixedCommission, setFixedCommission] = useState('');
  const [bonusThreshold, setBonusThreshold] = useState('');
  const [bonusRate, setBonusRate] = useState('');
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState({});
  const { theme } = useTheme();

  const calculateCommission = () => {
    if (!salesAmount || !commissionRate) {
      alert('Please fill in sales amount and commission rate');
      return;
    }

    const sa = parseFloat(salesAmount);
    const cr = parseFloat(commissionRate);
    const fc = parseFloat(fixedCommission) || 0;
    const bt = parseFloat(bonusThreshold) || 0;
    const br = parseFloat(bonusRate) || 0;

    if (isNaN(sa) || isNaN(cr)) {
      alert('Please enter valid numbers');
      return;
    }

    const baseCommission = (sa * cr) / 100;
    const bonus = sa > bt ? ((sa - bt) * br) / 100 : 0;
    const totalCommission = baseCommission + fc + bonus;

    setResult({
      baseCommission,
      fixedCommission: fc,
      bonus,
      totalCommission,
      commissionRate: cr,
      salesAmount: sa
    });

    // Update chart data
    const chartValues = {
      'Base Commission': baseCommission,
      'Fixed Commission': fc,
      'Bonus': bonus
    };

    setChartData({
      options: {
        chart: {
          type: 'donut',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        theme: {
          mode: theme === 'dark' ? 'dark' : 'light'
        },
        labels: Object.keys(chartValues),
        colors: ['#4CAF50', '#2196F3', '#FFC107'],
        legend: {
          position: 'bottom'
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      series: Object.values(chartValues)
    });
  };

  const features = [
    {
      icon: <FaMoneyBill className="text-green-500" />,
      title: 'Flexible Commission Calculation',
      description: 'Calculate base, fixed, and bonus commissions with ease'
    },
    {
      icon: <FaChartPie className="text-blue-500" />,
      title: 'Visual Breakdown',
      description: 'See how different components contribute to your total commission'
    },
    {
      icon: <FaPercentage className="text-purple-500" />,
      title: 'Custom Rates',
      description: 'Set your own commission rates and bonus thresholds'
    },
    {
      icon: <FaUsers className="text-yellow-500" />,
      title: 'Team Management',
      description: 'Track individual and team commission performance'
    }
  ];

  const benefits = [
    {
      title: 'Accurate Commission Tracking',
      description: 'Keep track of all commission components in one place'
    },
    {
      title: 'Motivation Tool',
      description: 'Use bonus thresholds to motivate higher sales'
    },
    {
      title: 'Financial Planning',
      description: 'Plan your finances with accurate commission projections'
    },
    {
      title: 'Performance Analysis',
      description: 'Analyze commission performance over time'
    }
  ];

  const faqs = [
    {
      question: 'What is a commission?',
      answer: 'A commission is a payment made to an employee or agent as an incentive to sell more of a product or service. It is typically calculated as a percentage of the sales amount.'
    },
    {
      question: 'How is commission calculated?',
      answer: 'Commission can be calculated in different ways: Base Commission = Sales Amount × Commission Rate. Bonus Commission = (Sales Amount - Bonus Threshold) × Bonus Rate. Total Commission = Base Commission + Fixed Commission + Bonus Commission.'
    },
    {
      question: 'What is a bonus threshold?',
      answer: 'A bonus threshold is the minimum sales amount required to earn bonus commission. It helps motivate sales representatives to achieve higher sales targets.'
    },
    {
      question: 'Can I use this calculator for team commissions?',
      answer: 'Yes, you can use this calculator for both individual and team commissions. Just enter the total sales amount and commission rates for your team.'
    }
  ];

  const howToUse = [
    {
      step: '1',
      title: 'Enter Sales Amount',
      description: 'Input your total sales amount for the period you want to calculate commission for.',
      example: 'Example: If you sold ₹50,000 worth of products, enter 50000'
    },
    {
      step: '2',
      title: 'Set Commission Rate',
      description: 'Enter your base commission rate as a percentage. This is the standard rate applied to all sales.',
      example: 'Example: If your commission rate is 10%, enter 10'
    },
    {
      step: '3',
      title: 'Add Fixed Commission (Optional)',
      description: 'If you have a fixed commission amount, enter it here. This is typically a flat rate added to your base commission.',
      example: 'Example: If you have a fixed commission of ₹2,000, enter 2000'
    },
    {
      step: '4',
      title: 'Set Bonus Threshold and Rate (Optional)',
      description: 'Enter your bonus threshold and bonus rate to calculate additional commission for exceeding targets.',
      example: 'Example: If your bonus threshold is ₹30,000 and bonus rate is 5%, enter 30000 and 5 respectively'
    },
    {
      step: '5',
      title: 'Calculate Commission',
      description: 'Click the "Calculate Commission" button to see your total commission breakdown.',
      example: 'Example: For SA = ₹50,000, CR = 10%, FC = ₹2,000, BT = ₹30,000, BR = 5%, your total commission would be ₹7,000'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>Commission Calculator - Calculate Your Sales Commission</title>
        <meta name="description" content="Calculate your sales commission with our advanced commission calculator. Supports base, fixed, and bonus commissions with visual breakdown." />
        <meta property="og:title" content="Commission Calculator" />
        <meta property="og:description" content="Calculate your sales commission with our advanced commission calculator. Supports base, fixed, and bonus commissions with visual breakdown." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://example.com/path-to-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Commission Calculator" />
        <meta name="twitter:description" content="Calculate your sales commission with our advanced commission calculator. Supports base, fixed, and bonus commissions with visual breakdown." />
        <meta name="twitter:image" content="https://example.com/path-to-image.jpg" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="calculator-header text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <FaCalculator className="text-4xl text-purple-500 dark:text-purple-400" />
              <h2 className="text-2xl font-bold ml-3">Commission Calculator</h2>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); calculateCommission(); }} className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaMoneyBill className="text-green-500 dark:text-green-400" />
                  <input
                    type="number"
                    placeholder="Sales Amount (₹)"
                    value={salesAmount}
                    onChange={(e) => setSalesAmount(e.target.value)}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaPercentage className="text-blue-500 dark:text-blue-400" />
                  <input
                    type="number"
                    placeholder="Commission Rate (%)"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(e.target.value)}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaMoneyBill className="text-yellow-500 dark:text-yellow-400" />
                  <input
                    type="number"
                    placeholder="Fixed Commission (₹)"
                    value={fixedCommission}
                    onChange={(e) => setFixedCommission(e.target.value)}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaChartPie className="text-purple-500 dark:text-purple-400" />
                  <input
                    type="number"
                    placeholder="Bonus Threshold (₹)"
                    value={bonusThreshold}
                    onChange={(e) => setBonusThreshold(e.target.value)}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>

                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <FaPercentage className="text-green-500 dark:text-green-400" />
                  <input
                    type="number"
                    placeholder="Bonus Rate (%)"
                    value={bonusRate}
                    onChange={(e) => setBonusRate(e.target.value)}
                    className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-purple-800 py-3 rounded-lg hover:from-purple-600 hover:to-pink-600 transition duration-300"
              >
                Calculate Commission
              </button>

              {result && (
                <div className="bg-purple-100 dark:bg-purple-900 p-4 rounded-lg mt-4">
                  <h3 className="font-semibold mb-2">Result:</h3>
                  <div className="space-y-2">
                    <p className="text-purple-800 dark:text-purple-400 text-lg">Base Commission: ₹{result.baseCommission.toLocaleString()}</p>
                    <p className="text-yellow-800 dark:text-yellow-400">Fixed Commission: ₹{result.fixedCommission.toLocaleString()}</p>
                    <p className="text-green-800 dark:text-green-400">Bonus: ₹{result.bonus.toLocaleString()}</p>
                    <p className="text-blue-800 dark:text-blue-400 font-bold mt-2">Total Commission: ₹{result.totalCommission.toLocaleString()}</p>
                  </div>
                </div>
              )}
            </form>
          </motion.div>
        </div>

        {/* Chart Section */}
        {Object.keys(chartData).length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6">Commission Breakdown</h3>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Chart
                    options={chartData.options}
                    series={chartData.series}
                    type="donut"
                    width={600}
                  />
                </div>
                <div className="space-y-6">
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Base Commission:</strong> Calculated as {result.commissionRate}% of sales amount (₹{result.salesAmount.toLocaleString()})
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Fixed Commission:</strong> A flat rate commission added to your base commission
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Bonus:</strong> Additional commission earned for exceeding bonus threshold
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 font-bold">
                    <strong>Total Commission:</strong> Sum of all commission components
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* How to Use Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">How to Use</h3>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <ol className="space-y-6">
              {howToUse.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="border-l-4 border-purple-500 pl-4"
                >
                  <div className="flex items-center mb-2">
                    <span className="text-2xl font-bold text-purple-500 mr-3">{step.step}</span>
                    <h4 className="font-semibold">{step.title}</h4>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">{step.description}</p>
                  <p className="text-green-500 dark:text-green-400 italic">{step.example}</p>
                </motion.div>
              ))}
            </ol>
          </div>
        </div>

        {/* Features Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
              >
                {feature.icon}
                <h4 className="mt-3 font-semibold">{feature.title}</h4>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
              >
                <h4 className="font-semibold mb-2">{benefit.title}</h4>
                <p className="text-gray-600 dark:text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Frequently Asked Questions</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-center">
                  <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                  <h4 className="font-semibold">{faq.question}</h4>
                </div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionCalculator;
